import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Boundary, EditingProject, ViewportState } from "../types/project";
import { formatFrames, frameStep, parseTimeToFrames } from "../core/time";
import { setBoundary } from "../core/trim";
import { normalizeViewport } from "../core/viewport";
import type { WaveformPyramid } from "../waveform/types";
import type { WaveformService } from "../waveform/service";
import { useDetailWaveform } from "../waveform/useDetailWaveform";
import type { PlaybackController } from "../audio/playback";
import { WaveformCanvas } from "./WaveformCanvas";

type Props = {
  boundary: Boundary;
  project: EditingProject;
  file: File;
  waveformService: WaveformService;
  overview: WaveformPyramid | null;
  playback: PlaybackController;
  onCancel: () => void;
  onConfirm: (frame: number) => void;
  onDetailTiming: (seconds: number) => void;
  onError: (message: string) => void;
};

const STEPS = [1, 10, 100, 1000] as const;
const PRECISION_HISTORY_MARKER = "voicecutter-precision";

function isPrecisionHistoryState(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "voicecutterPrecision" in value &&
    value.voicecutterPrecision === PRECISION_HISTORY_MARKER
  );
}

export function PrecisionEditor({
  boundary,
  project,
  file,
  waveformService,
  overview,
  playback,
  onCancel,
  onConfirm,
  onDetailTiming,
  onError,
}: Props) {
  const originalFrame =
    boundary === "start" ? project.startFrame : project.endFrame;
  const [temporaryFrame, setTemporaryFrame] = useState(originalFrame);
  const [stepMs, setStepMs] = useState<(typeof STEPS)[number]>(10);
  const [loop, setLoop] = useState(false);
  const [timeText, setTimeText] = useState(
    formatFrames(originalFrame, project.sampleRate),
  );
  const temporaryFrameRef = useRef(originalFrame);
  const repeatTimer = useRef<number | null>(null);
  const historyEntryActive = useRef(false);
  const pendingClose = useRef<"cancel" | "confirm" | null>(null);
  const onCancelRef = useRef(onCancel);
  const onConfirmRef = useRef(onConfirm);
  const viewport = useMemo<ViewportState>(
    () =>
      normalizeViewport(
        { centerFrame: temporaryFrame, visibleFrames: project.sampleRate * 10 },
        project.durationFrames,
        project.sampleRate,
      ),
    [temporaryFrame, project.durationFrames, project.sampleRate],
  );
  const detailView = useDetailWaveform({
    file,
    project,
    service: waveformService,
    centerFrame: temporaryFrame,
    visibleFrames: viewport.visibleFrames,
    overview,
    debounceMs: 60,
    onTiming: onDetailTiming,
  });

  useEffect(() => {
    onCancelRef.current = onCancel;
    onConfirmRef.current = onConfirm;
  }, [onCancel, onConfirm]);

  useEffect(() => {
    const currentState: unknown = window.history.state;
    const stateRecord =
      typeof currentState === "object" && currentState !== null
        ? currentState
        : {};
    window.history.pushState(
      {
        ...stateRecord,
        voicecutterPrecision: PRECISION_HISTORY_MARKER,
      },
      "",
      window.location.href,
    );
    historyEntryActive.current = true;
    const pop = (): void => {
      if (!historyEntryActive.current) return;
      historyEntryActive.current = false;
      const intent = pendingClose.current;
      pendingClose.current = null;
      if (intent === "confirm") onConfirmRef.current(temporaryFrameRef.current);
      else onCancelRef.current();
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);

  useEffect(
    () => () => {
      if (repeatTimer.current !== null) {
        window.clearTimeout(repeatTimer.current);
        window.clearInterval(repeatTimer.current);
        repeatTimer.current = null;
      }
      playback.stopPreview();
    },
    [playback],
  );

  const applyTemporary = useCallback(
    (frame: number) => {
      temporaryFrameRef.current = frame;
      setTemporaryFrame(frame);
      setTimeText(formatFrames(frame, project.sampleRate));
    },
    [project.sampleRate],
  );

  const updateTemporary = useCallback(
    (requested: number) => {
      const next = setBoundary(
        project,
        boundary,
        requested,
        project.durationFrames,
      );
      applyTemporary(boundary === "start" ? next.startFrame : next.endFrame);
    },
    [applyTemporary, boundary, project],
  );

  const nudge = useCallback(
    (direction: -1 | 1) => {
      const next = setBoundary(
        project,
        boundary,
        temporaryFrameRef.current +
          direction * frameStep(stepMs, project.sampleRate),
        project.durationFrames,
      );
      applyTemporary(boundary === "start" ? next.startFrame : next.endFrame);
    },
    [applyTemporary, boundary, project, stepMs],
  );

  const close = (intent: "cancel" | "confirm"): void => {
    if (
      historyEntryActive.current &&
      isPrecisionHistoryState(window.history.state)
    ) {
      pendingClose.current = intent;
      window.history.back();
      return;
    }
    historyEntryActive.current = false;
    if (intent === "confirm") onConfirmRef.current(temporaryFrameRef.current);
    else onCancelRef.current();
  };

  const beginRepeat = (direction: -1 | 1): void => {
    nudge(direction);
    stopRepeat();
    const delay = stepMs === 1 ? 150 : 280;
    repeatTimer.current = window.setTimeout(() => {
      repeatTimer.current = window.setInterval(
        () => nudge(direction),
        stepMs === 1 ? 90 : 65,
      );
    }, delay);
  };

  const stopRepeat = (): void => {
    if (repeatTimer.current !== null) {
      window.clearTimeout(repeatTimer.current);
      window.clearInterval(repeatTimer.current);
      repeatTimer.current = null;
    }
  };

  const preview = (): void => {
    const radius = Math.round(project.sampleRate * 1.5);
    void playback
      .playRange(
        Math.max(0, temporaryFrame - radius),
        Math.min(project.durationFrames, temporaryFrame + radius),
        loop,
      )
      .catch((error: unknown) =>
        onError(error instanceof Error ? error.message : String(error)),
      );
  };

  const commitText = (): void => {
    const parsed = parseTimeToFrames(
      timeText,
      project.sampleRate,
      project.durationFrames,
    );
    if (parsed === null)
      setTimeText(formatFrames(temporaryFrame, project.sampleRate));
    else updateTemporary(parsed);
  };

  return (
    <div
      className="modal precision"
      role="dialog"
      aria-modal="true"
      aria-labelledby="precision-title"
      data-testid="precision.dialog"
    >
      <header className="modal__header">
        <button
          type="button"
          className="text-button modal__cancel-top"
          onClick={() => close("cancel")}
        >
          キャンセル
        </button>
        <div>
          <p className={`boundary-kicker boundary-kicker--${boundary}`}>
            {boundary.toUpperCase()}
          </p>
          <h2 id="precision-title">
            {boundary === "start" ? "START位置を微調整" : "END位置を微調整"}
          </h2>
        </div>
        <span className="modal__header-spacer" />
      </header>

      <div className="precision__body">
        <div className="temporary-time" aria-live="polite">
          <span>仮の位置</span>
          <strong>{formatFrames(temporaryFrame, project.sampleRate)}</strong>
          <small>
            元の位置 {formatFrames(originalFrame, project.sampleRate)}
          </small>
        </div>

        <WaveformCanvas
          mode="detail"
          pyramid={detailView.pyramid}
          dataStartFrame={detailView.dataStartFrame}
          waveformSource={detailView.source}
          loading={detailView.loading}
          durationFrames={project.durationFrames}
          sampleRate={project.sampleRate}
          viewport={viewport}
          selection={
            boundary === "start"
              ? { startFrame: temporaryFrame, endFrame: project.endFrame }
              : { startFrame: project.startFrame, endFrame: temporaryFrame }
          }
          playheadFrame={temporaryFrame}
          onSeek={updateTemporary}
          label={`${boundary.toUpperCase()}位置の精密波形。中央の赤線が仮の境界です`}
        />
        <p className="canvas-hint">中央の赤線が設定される位置です</p>

        <div className="nudge-control" aria-label="境界位置の微調整">
          <HoldButton
            label={`マイナス${stepLabel(stepMs)}`}
            direction={-1}
            onStart={beginRepeat}
            onStop={stopRepeat}
          />
          <label className="step-picker">
            <span>1回で動かす量</span>
            <select
              value={stepMs}
              onChange={(event) =>
                setStepMs(Number(event.target.value) as (typeof STEPS)[number])
              }
              data-testid="precision.step"
            >
              {STEPS.map((step) => (
                <option key={step} value={step}>
                  {stepLabel(step)}
                </option>
              ))}
            </select>
          </label>
          <HoldButton
            label={`プラス${stepLabel(stepMs)}`}
            direction={1}
            onStart={beginRepeat}
            onStop={stopRepeat}
          />
        </div>
        <p className="hold-hint">− / ＋ は長押しで連続調整できます</p>

        <div className="precision__preview-row">
          <button
            type="button"
            className="secondary-button"
            onClick={preview}
            data-testid="precision.preview"
          >
            <span aria-hidden="true">▶</span> 境界を再生
          </button>
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={loop}
              onChange={(event) => setLoop(event.target.checked)}
              data-testid="precision.loop"
            />
            <span>ループ再生</span>
          </label>
        </div>

        <label className="time-entry">
          <span>時刻を直接入力</span>
          <input
            value={timeText}
            inputMode="decimal"
            aria-label="時刻を直接入力"
            onChange={(event) => setTimeText(event.target.value)}
            onBlur={commitText}
            onKeyDown={(event) => event.key === "Enter" && commitText()}
          />
          <small>hh:mm:ss.SSS</small>
        </label>
      </div>

      <footer className="precision__footer" data-testid="precision.footer">
        <button
          type="button"
          className="cancel-button"
          onClick={() => close("cancel")}
          data-testid="precision.cancel"
        >
          キャンセル
        </button>
        <button
          type="button"
          className="confirm-button"
          onClick={() => close("confirm")}
          data-testid="precision.confirm"
        >
          この位置に設定
        </button>
      </footer>
    </div>
  );
}

function HoldButton({
  label,
  direction,
  onStart,
  onStop,
}: {
  label: string;
  direction: -1 | 1;
  onStart: (direction: -1 | 1) => void;
  onStop: () => void;
}) {
  return (
    <button
      type="button"
      className="nudge-button"
      aria-label={label}
      data-testid={direction === -1 ? "precision.minus" : "precision.plus"}
      onPointerDown={() => onStart(direction)}
      onPointerUp={onStop}
      onPointerCancel={onStop}
      onPointerLeave={onStop}
      onContextMenu={(event) => event.preventDefault()}
    >
      {direction === -1 ? "−" : "＋"}
    </button>
  );
}

function stepLabel(step: number): string {
  return step === 1000 ? "1 秒" : `${step} ms`;
}
